import { useEffect, useRef, useState } from "react";
import { ExternalLink, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;
const clamp = (value) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

// Zoomable, pannable invoice image viewer — scroll/pinch to zoom, drag to pan
// while zoomed in. Sits inline next to the match-entry form so a user can
// glance at the scan and type the number without closing/reopening a modal.
export const InvoiceImageZoom = ({ src, alt = "Supplier invoice" }) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const containerRef = useRef(null);

  const zoomBy = (delta) => setScale((current) => {
    const next = clamp(current + delta);
    if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
    return next;
  });
  const reset = () => { setScale(MIN_SCALE); setOffset({ x: 0, y: 0 }); };

  // React attaches onWheel as a passive listener (can't preventDefault there),
  // which both logs a console warning and lets the page scroll while the
  // user is trying to zoom the image. A native listener with passive:false
  // fixes both.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handleWheel = (event) => { event.preventDefault(); zoomBy(event.deltaY > 0 ? -SCALE_STEP : SCALE_STEP); };
    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (event) => {
    if (scale <= MIN_SCALE) return;
    dragRef.current = { startX: event.clientX, startY: event.clientY, startOffset: offset };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const { startX, startY, startOffset } = dragRef.current;
    const bound = (containerRef.current?.clientWidth || 0) * scale;
    const next = { x: startOffset.x + (event.clientX - startX), y: startOffset.y + (event.clientY - startY) };
    setOffset({ x: Math.min(bound, Math.max(-bound, next.x)), y: Math.min(bound, Math.max(-bound, next.y)) });
  };
  const onPointerUp = () => { dragRef.current = null; setIsDragging(false); };

  return (
    <div className="flex h-full flex-col border border-slate-200 bg-slate-50" data-testid="invoice-image-zoom">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <span className="hidden text-[11px] font-medium text-slate-500 sm:inline" data-testid="invoice-zoom-hint">Scroll to zoom · drag to pan</span>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => zoomBy(-SCALE_STEP)} disabled={scale <= MIN_SCALE} data-testid="invoice-zoom-out-button"><ZoomOut size={14} /></Button>
          <span className="w-10 text-center text-xs tabular-nums text-slate-500" data-testid="invoice-zoom-level">{Math.round(scale * 100)}%</span>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => zoomBy(SCALE_STEP)} disabled={scale >= MAX_SCALE} data-testid="invoice-zoom-in-button"><ZoomIn size={14} /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={reset} disabled={scale === MIN_SCALE} data-testid="invoice-zoom-reset-button"><RotateCcw size={14} /></Button>
          <a href={src} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center text-slate-500 hover:text-slate-800" data-testid="invoice-image-open-new-tab"><ExternalLink size={14} /></a>
        </div>
      </div>
      <div
        ref={containerRef}
        className={`relative flex-1 overflow-hidden touch-none ${scale > MIN_SCALE ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onDoubleClick={() => (scale > MIN_SCALE ? reset() : zoomBy(SCALE_STEP * 2))}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="h-full w-full select-none object-contain"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: isDragging ? "none" : "transform 100ms ease-out" }}
          data-testid="supplier-invoice-image"
        />
      </div>
    </div>
  );
};
